// ===========================================
// BUSTED! BLACKJACK - AUTH STORE
// ===========================================

import { create } from 'zustand';
import { api } from '../api';

export interface User {
  id: string;
  username: string;
  discriminator: string | null;
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
  balance: number;
  isAdmin: boolean;
  provider: string;
  countryCode: string | null;
  ipAddress: string | null;
  equippedCosmetics: {
    chips: string | null;
    cards: string | null;
    table: string | null;
    sounds: string | null;
    badge: string | null;
  };
  lastDailyClaimAt: string | null;
  createdAt: string;
  tosAcceptedAt: string | null;
  tosVersion: string | null;
  tableSlug: string | null;
  /** Show play recommendations (basic strategy with counting deviations) */
  showRecommendations: boolean;
  /** Auto-execute NBA recommendations */
  autoPlay: boolean;
  /** Buddy subscription expiration date */
  buddyExpiresAt: string | null;
  /** Whether Buddy subscription is currently active */
  buddyActive: boolean;
  /** Hide seat tutorial modal on first seat join */
  hideSeatTutorial: boolean;
}

// Matches shared PlayerStats type for consistency with TablePage stats
export interface UserStats {
  /** Total hands played */
  handsPlayed: number;
  /** Win rate percentage (0-100) - pre-calculated by backend */
  winRate: number;
  /** Natural 21 count */
  blackjacks: number;
  /** Longest win streak */
  bestStreak: number;
  /** Longest losing streak */
  worstStreak: number;
  /** Net chips (won - lost) - pre-calculated by backend */
  netResult: number;
  /** Successful double-downs */
  doublesWon: number;
  /** Times split pairs */
  splitsPlayed: number;
}

interface AuthState {
  user: User | null;
  stats: UserStats | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;

  // Actions
  fetchUser: () => Promise<void>;
  fetchStats: () => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateBalance: (newBalance: number) => void;
  acceptTos: () => Promise<boolean>;
  setShowRecommendations: (enabled: boolean) => Promise<boolean>;
  setAutoPlay: (enabled: boolean) => Promise<boolean>;
  setHideSeatTutorial: (enabled: boolean) => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  stats: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,

  fetchUser: async () => {
    // Only set isLoading on initial load, not on refresh
    const isInitialLoad = !get().user;
    if (isInitialLoad) {
      set({ isLoading: true, error: null });
    }

    try {
      const response = await api.auth.me();

      if (response.success && response.data) {
        set({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false,
        });
        // Also fetch stats after user is loaded
        get().fetchStats();
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  fetchStats: async () => {
    try {
      const response = await api.users.getStats();
      if (response.success && response.data) {
        set({ stats: response.data.stats });
      }
    } catch {
      // Silently fail - stats are non-critical
    }
  },
  
  logout: async () => {
    try {
      await api.auth.logout();
    } catch {
      // Ignore errors, clear local state anyway
    }

    set({
      user: null,
      stats: null,
      isAuthenticated: false,
      error: null,
    });
  },
  
  clearError: () => {
    set({ error: null });
  },
  
  updateBalance: (newBalance: number) => {
    const { user } = get();
    if (user) {
      set({ user: { ...user, balance: newBalance } });
    }
  },

  acceptTos: async () => {
    try {
      const response = await api.auth.acceptTos();
      if (response.success && response.data) {
        set({ user: response.data.user });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },

  setShowRecommendations: async (enabled: boolean) => {
    const { user } = get();
    if (!user) return false;

    // Optimistically update UI
    set({ user: { ...user, showRecommendations: enabled } });

    try {
      const response = await api.users.updatePreferences({ showRecommendations: enabled });
      if (response.success) {
        // If disabling showRecommendations, also disable autoPlay
        if (!enabled && user.autoPlay) {
          set({ user: { ...get().user!, autoPlay: false } });
        }
        return true;
      }
      // Revert on failure
      set({ user: { ...user, showRecommendations: !enabled } });
      return false;
    } catch {
      // Revert on error
      set({ user: { ...user, showRecommendations: !enabled } });
      return false;
    }
  },

  setAutoPlay: async (enabled: boolean) => {
    const { user } = get();
    if (!user) return false;

    // Can't enable autoPlay without showRecommendations
    if (enabled && !user.showRecommendations) return false;

    // Optimistically update UI
    set({ user: { ...user, autoPlay: enabled } });

    // NOTE: Autoplay is now client-driven - server embeds recommendations
    // in TURN_STARTED/INSURANCE_OFFERED events. Client decides locally
    // whether to auto-execute based on this preference.

    try {
      const response = await api.users.updatePreferences({ autoPlay: enabled });
      if (response.success) {
        return true;
      }
      // Revert on failure
      set({ user: { ...user, autoPlay: !enabled } });
      return false;
    } catch {
      // Revert on error
      set({ user: { ...user, autoPlay: !enabled } });
      return false;
    }
  },

  setHideSeatTutorial: async (enabled: boolean) => {
    const { user } = get();
    if (!user) return false;

    // Optimistically update UI
    set({ user: { ...user, hideSeatTutorial: enabled } });

    try {
      const response = await api.users.updatePreferences({ hideSeatTutorial: enabled });
      if (response.success) {
        return true;
      }
      // Revert on failure
      set({ user: { ...user, hideSeatTutorial: !enabled } });
      return false;
    } catch {
      // Revert on error
      set({ user: { ...user, hideSeatTutorial: !enabled } });
      return false;
    }
  },
}));

export default useAuthStore;
